import React, { useState } from "react";
import { Topic } from "../../../services/instantNewsletterService";

interface TopicSwipeStepProps {
  topics: Topic[];
  onComplete: (keptIndices: number[]) => void;
  isLoading: boolean;
}

const REQUIRED_TOPICS = 3;

const ORIGINAL_CONTENT_TYPES = new Set(["personal_essay", "case_study"]);

const TYPE_LABELS: Record<string, string> = {
  curated_news: "Curated News",
  tactical_howto: "How-To",
  personal_essay: "Personal Take",
  case_study: "Case Study",
  resource_roundup: "Resources",
  trend_analysis: "Trend Analysis",
};

const TopicSwipeStep: React.FC<TopicSwipeStepProps> = ({ topics, onComplete, isLoading }) => {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

  const toggleTopic = (index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else if (next.size < REQUIRED_TOPICS) {
        next.add(index);
      }
      return next;
    });
  };

  const handleContinue = () => {
    // Convert to 1-based indices for the API
    const keptIndices = Array.from(selectedIndices).map((i) => i + 1);
    onComplete(keptIndices);
  };

  const remaining = REQUIRED_TOPICS - selectedIndices.size;

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", maxWidth: "440px", width: "100%" }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "3px solid #e0e0e0",
            borderTopColor: "#000",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 1.5rem",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <h3 style={{ fontSize: "1.2rem", marginBottom: "0.5rem" }}>
          Building your sections...
        </h3>
        <p style={{ color: "#666", fontSize: "0.9rem" }}>
          Generating newsletter sections based on your picks.
        </p>
      </div>
    );
  }

  return (
    <div style={{ textAlign: "center", maxWidth: "600px", width: "100%" }}>
      <h2 style={{ fontSize: "1.5rem", marginBottom: "0.25rem" }}>
        Which topics interest your audience?
      </h2>
      <p style={{ color: "#666", marginBottom: "1.25rem", fontSize: "0.9rem" }}>
        Select exactly {REQUIRED_TOPICS} topics to build your newsletter around.
        {remaining > 0 && ` (${remaining} more needed)`}
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "1.5rem" }}>
        {topics.map((topic, index) => {
          const isSelected = selectedIndices.has(index);
          const isDisabled = !isSelected && selectedIndices.size >= REQUIRED_TOPICS;
          return (
            <div
              key={index}
              onClick={() => !isDisabled && toggleTopic(index)}
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "18px 20px",
                border: isSelected ? "2px solid #000" : "1px solid #eee",
                cursor: isDisabled ? "not-allowed" : "pointer",
                opacity: isDisabled ? 0.45 : 1,
                transition: "border 0.15s ease, opacity 0.15s ease, box-shadow 0.15s ease",
                boxShadow: isSelected ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
                textAlign: "left",
                display: "flex",
                alignItems: "flex-start",
                gap: "14px",
              }}
            >
              {/* Toggle indicator */}
              <div
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "6px",
                  border: isSelected ? "2px solid #000" : "2px solid #ccc",
                  background: isSelected ? "#000" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: "2px",
                  transition: "all 0.15s ease",
                }}
              >
                {isSelected && (
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>

              {/* Topic content */}
              <div style={{ flex: 1 }}>
                <span
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    color: "#888",
                    marginBottom: "4px",
                    display: "block",
                  }}
                >
                  {TYPE_LABELS[topic.type] || topic.type}
                </span>
                <h3 style={{ fontSize: "1rem", marginBottom: "4px", lineHeight: 1.3 }}>
                  "{topic.headline}"
                </h3>
                <p style={{ color: "#555", fontSize: "0.85rem", lineHeight: 1.4, margin: 0 }}>
                  {topic.description}
                </p>
                {ORIGINAL_CONTENT_TYPES.has(topic.type) && (
                  <p style={{
                    fontSize: "0.75rem",
                    color: "#a0a0a0",
                    margin: "6px 0 0",
                    lineHeight: 1.3,
                  }}>
                    Requires a content interview or original material from your team.
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        onClick={handleContinue}
        disabled={selectedIndices.size !== REQUIRED_TOPICS}
        className="secondary-button"
        style={{
          width: "100%",
          padding: "12px",
          fontSize: "0.95rem",
          whiteSpace: "nowrap",
          maxWidth: "none",
          opacity: selectedIndices.size !== REQUIRED_TOPICS ? 0.5 : 1,
        }}
      >
        Continue with {selectedIndices.size} / {REQUIRED_TOPICS} selected
      </button>
    </div>
  );
};

export default TopicSwipeStep;
